import React from 'react';
import { Typography, Box, Card, CardContent, CircularProgress, alpha } from '@mui/material';

interface StatisticsCardProps {
    title: string;
    value: number | string | null;
    loading: boolean;
    color?: string;
    icon?: React.ReactNode;
    subtitle?: string;
    onClick?: () => void;
}

const StatisticsCard: React.FC<StatisticsCardProps> = ({
    title,
    value,
    loading,
    color = 'primary.main',
    icon,
    subtitle,
    onClick
}) => {
    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 2,
                borderRadius: 2,
                transition: 'transform 0.3s, box-shadow 0.3s',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': onClick ? {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                    cursor: 'pointer'
                } : {},
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    backgroundColor: color,
                }
            }}
            onClick={onClick}
        >
            <CardContent sx={{
                flexGrow: 1,
                p: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
                bgcolor: (theme) => alpha(theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.common.white, 0.9)
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography 
                        variant="h6" 
                        color="text.secondary" 
                        fontWeight={500}
                        sx={{
                            fontSize: { xs: '0.75rem', sm: '0.85rem', md: '1rem', lg: '1.1rem' }
                        }}
                    >
                        {title}
                    </Typography>
                    {icon && (
                        <Box sx={{
                            color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: (theme) => alpha(color, 0.1),
                            borderRadius: '50%',
                            width: { xs: 28, sm: 32, md: 36, lg: 40 },
                            height: { xs: 28, sm: 32, md: 36, lg: 40 },
                            '& .MuiSvgIcon-root': {
                                fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem', lg: '1.8rem' }
                            }
                        }}>
                            {icon}
                        </Box>
                    )}
                </Box>

                {loading ? (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: { xs: '40px', sm: '50px', md: '60px' } 
                    }}>
                        <CircularProgress 
                            sx={{ 
                                width: { xs: '24px !important', sm: '28px !important', md: '32px !important' },
                                height: { xs: '24px !important', sm: '28px !important', md: '32px !important' }
                            }}
                            color="inherit" 
                        />
                    </Box>
                ) : (
                    <>
                        <Typography 
                            variant="h3" 
                            component="div" 
                            sx={{ 
                                fontWeight: 'bold', 
                                color,
                                fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.2rem', lg: '2.5rem' }
                            }}
                        >
                            {value !== null ? value : '-'}
                        </Typography>

                        {subtitle && (
                            <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                sx={{ 
                                    mt: 1,
                                    fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.8rem', lg: '0.85rem' }
                                }}
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default StatisticsCard;
